// Folder: src/components/channels/
// File: ChannelSelector.tsx
// Change #13 - 2025-01-15 11:00:00Z: Fix TypeError - channels.find is not a function

import React from 'react';
import { ChevronDown, Youtube, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';

// Updated interface to match the store's Channel interface
// Change #13: Added more flexible interface to handle API response variations
interface Channel {
  // id: string; 
  name: string;
  channel_name?: string;  // ← ADD THIS LINE
  channel_id: string;
  thumbnail?: string;
  thumbnail_url?: string; // Add this for API compatibility
  subscriber_count?: number | string; // Allow both number and string
  project_count?: number;
  last_activity?: string;
  youtube_authenticated?: boolean;
  scheduling_enabled?: boolean;
  // Additional fields from YouTube API
  description?: string;
  video_count?: string;
  view_count?: string;
  custom_url?: string;
  country?: string;
  default_language?: string;
}

interface ChannelSelectorProps {
  channels: Channel[] | ChannelsApiResponse | undefined | null;
  selectedChannel: string | null;
  onSelectChannel: (channelId: string) => void;
  onAddChannel?: () => void;
  className?: string;
}
interface ChannelsApiResponse {
  channels: Channel[];
  total: number;
  user_id: string;
  timestamp: string;
  optimized: boolean;
}

export const ChannelSelector: React.FC<ChannelSelectorProps> = ({
  channels,
  selectedChannel,
  onSelectChannel,
  onAddChannel,
  className = ''
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  
  // Change #13: Add safety check and default to empty array
  const safeChannels = React.useMemo(() => {
  if (!channels) {
    console.warn('ChannelSelector: channels is null or undefined:', channels);
    return [];
  }

  // If it's already an array, use it directly
  if (Array.isArray(channels)) {
    return channels;
  }

  // If it's an API response object with channels property, extract the array
  if (typeof channels === 'object' && 'channels' in channels && Array.isArray(channels.channels)) {
    console.log('ChannelSelector: Using channels from API response object');
    return channels.channels;
  }

  // Fallback for unexpected format
  console.warn('ChannelSelector: channels is not an array or valid response object:', channels);
  return [];
}, [channels]);
  
  // Change #13: Use safeChannels instead of channels
  const selectedChannelData = safeChannels.find(c => c.channel_id === selectedChannel);

  return (
    <div className={`relative ${className}`}>
      {/* Trigger Button */}
      <Button
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full justify-between"
        rightIcon={<ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
      >
        <div className="flex items-center space-x-2">
          <Youtube className="w-4 h-4 text-red-600" />
          <span>
            {selectedChannelData ? 
              (selectedChannelData.channel_name || selectedChannelData.name || `Channel ${selectedChannelData.channel_id.slice(0, 8)}...`) : 
              'Select Channel'
            }
          </span>
        </div>
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
          {/* Change #13: Use safeChannels instead of channels */}
          {safeChannels.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <Youtube className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm">No channels connected</p>
              {onAddChannel && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onAddChannel();
                    setIsOpen(false);
                  }}
                  className="mt-2"
                  leftIcon={<Plus className="w-4 h-4" />}
                >
                  Connect Channel
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Change #13: Use safeChannels and add error handling */}
              {safeChannels.map((channel) => (
                <div
                  key={channel.channel_id}
                  onClick={() => {
                    onSelectChannel(channel.channel_id);
                    setIsOpen(false);
                  }}
                  className={`p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-600 last:border-b-0 ${
                    selectedChannel === channel.channel_id ? 'bg-purple-50 dark:bg-purple-900/20' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {/* Change #13: Handle both thumbnail and thumbnail_url */}
                      {(channel.thumbnail || channel.thumbnail_url) ? (
                        <img
                          src={channel.thumbnail || channel.thumbnail_url}
                          alt={channel.name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                          <Youtube className="w-4 h-4 text-red-600" />
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {channel.channel_name || channel.name || `Channel ${channel.channel_id.slice(0, 8)}...`}
                        </p>
                        <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                          {/* Change #13: Handle both number and string subscriber_count */}
                          {channel.subscriber_count && (
                            <span>
                              {typeof channel.subscriber_count === 'number' 
                                ? channel.subscriber_count.toLocaleString() 
                                : parseInt(channel.subscriber_count.toString() || '0').toLocaleString()
                              } subscribers
                            </span>
                          )}
                          {channel.project_count !== undefined && (
                            <span>{channel.project_count} projects</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col space-y-1">
                      <Badge
                        variant={channel.youtube_authenticated ? 'success' : 'error'}
                        size="sm"
                      >
                        {channel.youtube_authenticated ? 'Connected' : 'Disconnected'}
                      </Badge>
                      
                      {channel.scheduling_enabled && (
                        <Badge variant="default" size="sm">
                          Auto-Schedule
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {onAddChannel && (
                <div
                  onClick={() => {
                    onAddChannel();
                    setIsOpen(false);
                  }}
                  className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-t border-gray-200 dark:border-gray-600"
                >
                  <div className="flex items-center space-x-3 text-purple-600 dark:text-purple-400">
                    <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                      <Plus className="w-4 h-4" />
                    </div>
                    <span className="font-medium">Connect New Channel</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      
      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};